/* The rules behind /u1/plans/paymentdetails, transcribed from components/public/Payment/Plans/PaymentDetails.jsx and PaymentsDetailsForm.jsx. */

import { PROFILE_WINDOW_DAYS } from "../../../../constants/webProfilePricing";

/* localStorage is a shared, user-writable surface: a hand-edited or
   half-written value must not take the checkout down. Every read is guarded
   the way the live page guards its two. */
export const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

/* TIP-493 (B3) — route state is lost on refresh, so a deep-load falls back to
   the id CreateCompany/PaymentPlans stored before navigating here. */
export const resolveCompanyId = (state) =>
  state?.company_id || readJson("payasyougoCompanyID");

/* TIP-556 — a report order has no company yet; the webhook creates it after
 * payment, so there is no company_id to carry.
 *
 * TIP-564 — the localStorage flag is trusted ONLY when there is no route state
 * at all (a true deep-load). Any in-app navigate() passes a state object, even
 * for an unrelated purchase, so it must not inherit a stale flag left behind by
 * an abandoned report order.
 */
export const isReportRequestFrom = (state) =>
  state
    ? Boolean(state.reportRequest)
    : localStorage.getItem("createWorldboxCompany") === "true";

/* The name typed into the order form, shown in place of a company record that
   does not exist yet. */
export const requestedCompanyNameFor = (isReportRequest) =>
  isReportRequest ? readJson("companyDescription")?.companyname || null : null;

/* Transcribed precedence: the plan carried in route state, else the mode
   stashed before the last Stripe round-trip, else pay-as-you-go. */
export const resolveMode = (planDetails) =>
  planDetails?.mode || localStorage.getItem("webProfilePurchaseMode") || "payg";

/* One line under the panel heading saying what is being charged. Wording
   transcribed verbatim — it is the only thing telling a member why the price
   dropped. */
export const modeCopy = (mode) => {
  if (mode === "bundle") {
    return "You will be charged for Membership and the Web Profile together. The Member rate applies from this purchase forward.";
  }
  if (mode === "member") {
    return `Your Member rate applies. ${PROFILE_WINDOW_DAYS} days of full claim.`;
  }
  return "One-time payment for the Web Profile. No Membership required.";
};

/* The upsell panel is offered on a standard-rate purchase only: there is
   nothing to upsell to a member, and a bundle is already buying it. */
export const showsUpsell = (mode) => mode === "payg";

/* The name shown at the top of the summary, in the live order of preference. */
/* The company parked when the purchase started, for when the lookup cannot find it. */
export const parkedCompany = () => readJson("payasyougoCompany") || {};

/* `company?.company?.name` is the WorldBox shape — the same fallback the
   profile masthead uses — so a record that DID load in that shape names itself
   rather than falling through to the parked copy. */
export const summaryCompanyName = (company, fallbackName) =>
  company?.registered_company_name ||
  company?.company_name ||
  company?.company?.name ||
  fallbackName ||
  parkedCompany().name ||
  "this business";

/* Full country name from a 2-letter code. Intl.DisplayNames is missing on
   older browsers, so the code itself is the fallback — as the live summary
   does. */
export const countryNameOf = (code) => {
  const cc = String(code || "").toUpperCase();
  if (!cc) return "";
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(cc) || cc;
  } catch (e) {
    return cc;
  }
};

export const companyCountryCode = (company) =>
  String(
    company?.country_code ||
      company?.country ||
      /* WorldBox shape, then the copy parked at purchase — without these a
         listing showed no flag and no country beside its name. */
      company?.company?.countrycode ||
      parkedCompany().country_code ||
      ""
  ).toUpperCase();

/* The primary billing address, or the first one on file. */
export const primaryAddressOf = (addresses) => {
  const list = Array.isArray(addresses) ? addresses : [];
  return list.filter((a) => a?.is_primary)[0] || list[0] || undefined;
};

/* The list actually handed to Stripe as the shipping address: the primary one
   if there is one, otherwise everything on file — matching `shippingAddress`
   in the live form, which CheckoutForm reads by index [0]. */
export const shippingAddressesOf = (addresses) => {
  const list = Array.isArray(addresses) ? addresses : [];
  const primary = list.filter((a) => a?.is_primary);
  return primary.length > 0 ? primary : list;
};

/* One line of an address, in the live field order, with the blanks dropped so
   a missing line 2 does not leave a double space. */
export const addressLineOf = (address) =>
  [
    address?.address_line_1,
    address?.address_line_2,
    address?.city,
    address?.postal_code,
    address?.country,
  ]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean)
    .join(", ");

/* What create_payment is told about this purchase.
 *
 * TIP-564 — createWorldBoxCompanyData and companyDescription belong to the
 * report-order flow ONLY. Sending them unconditionally let a stale flag from an
 * abandoned report order ride along on a later, unrelated purchase.
 */
export const paymentMetaData = ({
  billingAddressId,
  userId,
  isReportRequest,
} = {}) => ({
  createWorldbox: localStorage.getItem("createWorldbox"),
  worldboxdata: localStorage.getItem("worldboxData"),
  companyDataString: readJson("payasyougoCompanyID"),
  paycompanyStatus: localStorage.getItem("paycompanyStatus"),
  billing_add_ID: billingAddressId,
  UserID: userId,
  ...(isReportRequest && {
    createWorldBoxCompanyData: localStorage.getItem("createWorldboxCompany"),
    companyDescription: localStorage.getItem("companyDescription"),
  }),
});

/* The create_payment body. `bundle` mode is the only one that subscribes, and
   the plan is sent only then — the other two are one-time invoices. */
export const createPaymentBody = ({
  mode,
  membershipPlan,
  actingCompanyId,
  purchaseTrackId,
  metaData,
}) => {
  const isBundle = mode === "bundle";
  return {
    recurring: isBundle,
    bundle: isBundle,
    plan: isBundle ? membershipPlan : undefined,
    product_type: "PAY-AS-YOU-GO",
    acting_company_id: actingCompanyId,
    promocode: "",
    purchase_track_id: purchaseTrackId || "",
    meta_data: metaData,
  };
};

/* What finalize returned, named. */
export const finalizeOutcome = (clientSecret) => {
  if (clientSecret === "PAID") return "paid";
  if (clientSecret) return "ready";
  return "failed";
};
